import {recordRumAction} from '../../util/datadog/datadogRum';
import {getConversionUnitFromMaxValue} from '../../util/plotHelpers';
import {type TimestampKeyTuple} from './types';

type BucketedHistoryRow = Record<string, number | undefined> & {
  _timestampLast: number;
};

type ConvertBucketedHistoryOpts = {
  cacheKey: string;
  creationTimestamp: number;
  history: BucketedHistoryRow[];
  safeXAxis: string;
  xAxis: string;
  zoom: Record<string, number>;
};

export function buildIssueTimestampsBucketedHistorySpec({
  xAxis,
  zoom,
}: {
  xAxis: string;
  zoom: [number | null, number | null];
}): string {
  const propTarget = xAxis === '_step' ? 'Step' : 'X';
  const zoomObj: Record<string, number> = {};
  if (zoom[0] != null) {
    zoomObj[`min${propTarget}`] = Math.floor(zoom[0]);
  }
  if (zoom[1] != null) {
    zoomObj[`max${propTarget}`] = Math.ceil(zoom[1]);
  }
  const safeXAxis = xAxis === '_absolute_runtime' ? '_runtime' : xAxis;

  return JSON.stringify({
    keys: ['_timestamp', safeXAxis],
    samples: 1000,
    bins: 1000,
    ...zoomObj,
    xAxis: safeXAxis,
    metricTransformations: {},
  });
}

export function convertBucketedHistoryToTimestampTuples({
  cacheKey,
  creationTimestamp,
  history,
  safeXAxis,
  xAxis,
  zoom,
}: ConvertBucketedHistoryOpts): TimestampKeyTuple[] {
  const timestampTuples: TimestampKeyTuple[] = [];

  const mapFnToAxis: Record<
    string,
    ({
      timestamp,
      xValue,
      index,
      maxVal,
    }: {
      timestamp: number;
      maxVal?: number;
      xValue: number;
      index: number;
    }) => void
  > = {
    _runtime: ({timestamp, xValue, maxVal}) => {
      if (typeof maxVal !== 'number') {
        recordRumAction('Issues_Observability_Error', {
          message: 'No Max val in _runtime issue transformation',
          xAxis,
          safeXAxis,
          cacheKey,
          zoom,
          timestamp,
          xValue,
          maxVal,
        });
        return;
      }
      const unit = getConversionUnitFromMaxValue(maxVal);
      timestampTuples.push([timestamp, xValue / unit]);
    },
    _absolute_runtime: ({timestamp, xValue, maxVal}) => {
      if (typeof maxVal !== 'number') {
        recordRumAction('Issues_Observability_Error', {
          message: 'No Max val in _runtime issue transformation',
          xAxis,
          safeXAxis,
          cacheKey,
          zoom,
          timestamp,
          xValue,
          maxVal,
        });
        return;
      }
      const unit = getConversionUnitFromMaxValue(maxVal);
      timestampTuples.push([timestamp, xValue / unit]);
    },
    _timestamp: ({timestamp, xValue}) => {
      timestampTuples.push([timestamp, xValue * 1000]);
    },
    default: ({timestamp, xValue, index}) => {
      if (
        index > 0 &&
        index - 1 < timestampTuples.length &&
        timestampTuples[index - 1] &&
        (timestamp <= timestampTuples[index - 1][0] ||
          xValue <= timestampTuples[index - 1][1])
      ) {
        recordRumAction('Issues_Observability_Error', {
          message:
            'Returned unsorted on nonmonotonic values from issuesTimestamp Query',
          xAxis,
          safeXAxis,
          cacheKey,
          zoom,
          timestamp,
          xValue,
          index,
        });
      }

      timestampTuples.push([timestamp, xValue]);
    },
  };

  const maxFn: Record<
    string,
    (rows: BucketedHistoryRow[]) => number | undefined
  > = {
    _runtime: rows => {
      return Math.max(...rows.map(curr => curr['_runtimeLast'] ?? 0));
    },
    _absolute_runtime: rows => {
      return Math.max(
        ...rows.map(
          curr =>
            ((curr['_timestampLast'] ?? 0) * 1000 - creationTimestamp) / 1000
        )
      );
    },
    _timestamp: () => undefined,
    default: () => undefined,
  };

  if (
    xAxis === '_runtime' ||
    xAxis === '_absolute_runtime' ||
    xAxis === '_timestamp' ||
    xAxis === '_step'
  ) {
    timestampTuples.push([creationTimestamp, 0]);
  }

  const maxVal = maxFn[xAxis]?.(history) ?? undefined;
  const mapFn = mapFnToAxis[xAxis] ?? mapFnToAxis['default'];

  history.forEach((curr, index) => {
    const currentTimestampMs = (curr._timestampLast ?? 0) * 1000;
    const xValue = curr[`${safeXAxis}Last`];
    if (currentTimestampMs == null || xValue == null) {
      recordRumAction('Issues_Observability_Error', {
        message: 'Returned incorrect data from issuesTimestamp Query',
        xAxis,
        safeXAxis,
        cacheKey,
        zoom,
        timestamp: currentTimestampMs,
        xValue,
      });
      return;
    }

    mapFn({
      timestamp: currentTimestampMs,
      xValue,
      index,
      maxVal,
    });
  });

  return timestampTuples;
}
